import axios from 'axios';

// Configure axios base URL to work with the existing backend
// Automatically detect if we're running on external IP and adjust accordingly
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // If we're accessing via external IP, use the same host for API
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // External IP access - use port 9001 for backend
    return `http://${hostname}:9001/api/v1`;
  }
  
  // Local development - use relative URL (will use proxy)
  return '/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

// Log the API URL being used
console.log('API Base URL:', API_BASE_URL);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 1 minute timeout - reduced from 2 minutes
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for cookie-based authentication
});

// Request interceptor for debugging
apiClient.interceptors.request.use((config) => {
  console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Token refresh state management (shared with authApi)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry if this is already a refresh or auth endpoint
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => apiClient(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token using the auth endpoint
        const hostname = window.location.hostname;
        const authBaseUrl = hostname !== 'localhost' && hostname !== '127.0.0.1'
          ? `http://${hostname}:9001/api/v1`
          : '/api/v1';

        await axios.post(`${authBaseUrl}/auth/refresh`, {}, { withCredentials: true });

        processQueue();
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Redirect to login if refresh fails
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export interface SearchOptions {
  max_results?: number;
  include_explanation?: boolean;
  global_search?: boolean;
  use_rag?: boolean;
  sources?: string[];
  filters?: Record<string, any>;
}

export interface IrregularitySearchRequest {
  query: string;
  threshold?: number;
  max_results?: number;
  filters?: Record<string, any>;
}

export interface IrregularityType {
  id: string;
  name: string;
  description: string;
  category: string;
}

// Vendor Verification API Types
export interface VendorSearchRequest {
  query: string;
  search_type?: 'name' | 'uei' | 'auto';
  filters?: {
    location?: string;
    naics?: string;
  };
  limit?: number;
  sort_by?: 'relevance' | 'award_count';
}

export interface FraudIndicator {
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  score_impact: number;
  evidence?: any;
  details?: any; // Backward compatibility
}

export interface RiskAnalysis {
  risk_score: number;
  risk_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  risk_indicators: FraudIndicator[];
  fraud_probability: number;
  investigation_priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  requires_investigation: boolean;
  // Backward compatibility fields
  total_risk_score?: number;
  risk_factors?: string[];
  fraud_risk_score?: number;
  fraud_risk_level?: string;
  fraud_indicators_count?: number;
}

export interface CompetitionAnalysis {
  sole_source_count: number;
  sole_source_percentage: number;
  full_competition_count: number;
  full_competition_percentage: number;
  avg_competition_count: number;
  awards_with_data: number;
  total_awards: number;
  competition_rate: number;
}

export interface VendorBasicInfo {
  legal_business_name: string;
  uei_sam: string;
  registration_status: string;
  registration_date: string;
  uei_status?: string;
  cage_code: string;
  entity_url?: string;
  city?: string;
  state_code?: string;
  primary_naics?: string;
  match_score: number;
  award_count?: number;
  risk_analysis?: RiskAnalysis;
  competition_analysis?: CompetitionAnalysis;
}

// Standard search API
export const searchAPI = {
  async standardSearch(query: string, options: SearchOptions = {}) {
    const response = await apiClient.post('/nl-search', {
      query,
      max_results: options.max_results || 30,
      include_explanation: options.include_explanation !== false,
      global_search: options.global_search || false,
      use_rag: options.use_rag !== undefined ? options.use_rag : true,
      sources: options.sources,
      filters: options.filters,
    });
    return response.data;
  },
};

// Irregularity-specific search API
export const irregularityAPI = {
  async searchByType(
    irregularityType: string,
    query: string,
    options: Omit<IrregularitySearchRequest, 'query'> = {}
  ) {
    const response = await apiClient.post(`/${irregularityType}-search`, {
      query,
      threshold: options.threshold || 5,
      max_results: options.max_results || 50,
      filters: options.filters,
    });
    return response.data;
  },

  async getTypes(): Promise<{ irregularity_types: IrregularityType[] }> {
    const response = await apiClient.get('/irregularity-types');
    return response.data;
  },
};

// Deep Research API
export const deepResearchAPI = {
  async createReport(request: any) {
    const response = await apiClient.post('/deep-research/create', request);
    return response.data;
  },

  async getReport(reportId: string) {
    const response = await apiClient.get(`/deep-research/${reportId}`);
    return response.data;
  },

  async listReports(params: { status?: string; limit?: number } = {}) {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await apiClient.get(`/deep-research/reports?${queryParams.toString()}`);
    return response.data;
  },

  async getHealth() {
    const response = await apiClient.get('/deep-research/health');
    return response.data;
  },

  async exportToPdf(reportId: string) {
    const response = await apiClient.get(`/deep-research/${reportId}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getPrompts() {
    const response = await apiClient.get('/deep-research/config/prompts');
    return response.data;
  },

  async updatePrompt(promptType: string, data: { template: string; created_by: string }) {
    const response = await apiClient.put(`/deep-research/config/prompts/${promptType}`, data);
    return response.data;
  },
};

// Vendor Verification API
export const vendorAPI = {
  async searchVendors(request: VendorSearchRequest) {
    const response = await apiClient.post('/vendor/search', request);
    return response.data;
  },

  async getVendorDetails(uei: string) {
    const response = await apiClient.get(`/vendor/${uei}/details`);
    return response.data;
  },

  async getVendorAwards(uei: string, source = 'all', page = 1, perPage = 50) {
    const response = await apiClient.get(`/vendor/${uei}/awards`, {
      params: { source, page, per_page: perPage }
    });
    return response.data;
  },

  async getVendorSummary(uei: string, includeLegal: boolean = false) {
    const response = await apiClient.get(`/vendor/${uei}/summary`, {
      params: { include_legal: includeLegal }
    });
    return response.data;
  },

  async verifyVendor(uei: string, contractType?: string, naics?: string) {
    const response = await apiClient.post(`/vendor/${uei}/verify`, null, {
      params: { contract_type: contractType, naics }
    });
    return response.data;
  },

  async bulkVerifyVendors(ueis: string[]) {
    const response = await apiClient.post('/vendor/bulk-verify', { ueis });
    return response.data;
  },

  async getHealth() {
    const response = await apiClient.get('/vendor/health');
    return response.data;
  },

  async getRiskAnalysis(uei: string) {
    const response = await apiClient.get(`/vendor/${uei}/risk-analysis`);
    return response.data;
  },

  async getVendorLaborViolations(uei: string, vendorName?: string, legalName?: string) {
    const response = await apiClient.get(`/vendor/${uei}/labor-violations`, {
      params: {
        vendor_name: vendorName,
        legal_name: legalName
      }
    });
    return response.data;
  },

  async getVendorCompliance(uei: string, vendorName?: string, legalName?: string) {
    const response = await apiClient.get(`/vendor/${uei}/compliance`, {
      params: {
        vendor_name: vendorName,
        legal_name: legalName
      }
    });
    return response.data;
  }
};

// OFAC screening endpoints
export const ofacAPI = {
  async screenVendor(uei: string, name: string) {
    const response = await apiClient.post('/vendor/ofac-screen', { uei, name });
    return response.data;
  },

  async batchScreen(vendors: Array<{uei: string, name: string}>) {
    const response = await apiClient.post('/vendor/batch-ofac-screen', { vendors });
    return response.data;
  },

  async getStatus(uei: string) {
    const response = await apiClient.get(`/vendor/${uei}/ofac-status`);
    return response.data;
  },

  async syncData() {
    const response = await apiClient.post('/admin/ofac-sync');
    return response.data;
  },

  async reportFalsePositive(uei: string, details: any) {
    const response = await apiClient.post('/vendor/ofac-false-positive', { uei, details });
    return response.data;
  },

  async getComplianceStats() {
    const response = await apiClient.get('/admin/ofac-compliance-stats');
    return response.data;
  },

  async getBulkJobStatus(jobId: string) {
    const response = await apiClient.get(`/vendor/ofac-bulk-job/${jobId}`);
    return response.data;
  }
};

// Fraud analysis utilities
export const fraudUtils = {
  getRiskColor(riskLevel: string): string {
    switch (riskLevel.toUpperCase()) {
      case 'CRITICAL':
      case 'HIGH':
        return '#f44336';
      case 'MEDIUM':
        return '#ff9800';
      case 'LOW':
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  },

  getRiskIcon(riskLevel: string): string {
    switch (riskLevel.toUpperCase()) {
      case 'CRITICAL':
        return '🚨';
      case 'HIGH':
        return '⚠️';
      case 'MEDIUM':
        return '⚠️';
      case 'LOW':
        return '✅';
      default:
        return '❓';
    }
  },

  formatCurrency(amount: number): string {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  },

  generateUSASpendingLink(contract: any): string {
    if (contract.award_id) {
      return `https://www.usaspending.gov/award/${contract.award_id}`;
    }
    // Fallback search by vendor and amount
    const vendor = encodeURIComponent(contract.vendor_name || '');
    const amount = contract.total_obligated_amount || contract.amount || 0;
    return `https://www.usaspending.gov/search/?hash=1a4e4b5d4a4a5b&filters=${JSON.stringify({
      recipient_name: vendor,
      award_amounts: amount,
    })}`;
  },
};

export default apiClient;